import { Button, Divider } from '@mui/material';
import { blue } from '@mui/material/colors';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../../../trpc/react';
import { MultiSelect } from '../../../components/input/multi-select';
import { SearchInput } from '../../../components/input/search-input';
import { useViewer } from '../../../context/viewer';
import { DisabledWithTooltip } from '../../../components/permissions/DisabledWithTooltip';
import { Tag } from '../../library/tags/types';
import { ExerciseCard } from '../../library/exercises/components/ExerciseCard';
import {
  BodyPart,
  Equipment,
  LibraryExercise,
  TrainingType,
} from '../../library/exercises/types';
import { AdvancedSearchModal } from './AdvancedSearchModal';
import { AdvancedSearchTable } from './AdvancedSearchTable';

export const AdvancedExerciseSearchModal = ({
  id,
  initialSearchTerm,
  open,
  onClose,
  onAddExercise,
  setOpenCreateExerciseSheet,
  teamId,
  baseExerciseId,
  excludeExerciseIds,
}: {
  id: string;
  initialSearchTerm: string;
  open: boolean;
  onClose: () => void;
  onAddExercise: (exercise: LibraryExercise) => void;
  setOpenCreateExerciseSheet?: (searchTerm: string) => void;
  teamId: string;
  /**
   * Alternate-picker-only search inputs. When set (the "See More" flow from the
   * alternate picker, flag on), results are overlap-ranked against
   * `baseExerciseId`, the group's members are excluded, and each card shows its
   * movement-tag chips. Omitted by the primary picker / swap flow / flag off, so
   * the modal behaves exactly as before.
   */
  baseExerciseId?: string;
  excludeExerciseIds?: string[];
}) => {
  const { t } = useTranslation();
  const { hasPermissions } = useViewer();
  const canCreateExercise = hasPermissions('library.exercises', 'create');
  const [searchTerm, setSearchTerm] = useState<string>(initialSearchTerm);
  const [types, setTypes] = useState<TrainingType[]>([]);
  const [selectedBodyParts, setSelectedBodyParts] = useState<BodyPart[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);

  const bodyPartsQuery = trpc.bodyPart.fetch.useQuery({});
  const bodyParts = bodyPartsQuery.data || [];
  const equipmentQuery = trpc.equipment.fetch.useQuery({});
  const equipment = equipmentQuery.data || [];
  const trainingTypesQuery = trpc.trainingType.fetch.useQuery({});
  const trainingTypes = trainingTypesQuery.data || [];
  const tagsQuery = trpc.tag.tags.useQuery({
    types: ['exercise'],
  });
  const tags = tagsQuery.data || [];

  const exercisesQuery = trpc.exerciseLibrary.exerciseSearch.useQuery({
    searchTerm,
    teamId,
    trainingTypeIds: types.map((type) => type.id),
    bodyPartIds: selectedBodyParts.map((bp) => bp.id),
    equipmentIds: selectedEquipment.map((eq) => eq.id),
    tagIds: selectedTags.map((tag) => tag.id),
    limit: 100,
    baseExerciseId,
    excludeExerciseIds,
  });
  const exercises = exercisesQuery.data?.records || [];

  // Tag chips + overlap ranking + exclusion are the alternate-picker context,
  // signalled by the presence of a base exercise (mirrors the inline picker).
  const showTags = !!baseExerciseId;

  return (
    <AdvancedSearchModal<LibraryExercise>
      id={id}
      modalTitle={t('addExercise')}
      galleryTitle={t('exercises')}
      titleActions={
        setOpenCreateExerciseSheet ? (
          <DisabledWithTooltip
            disabled={!canCreateExercise}
            reason={t('permissionDisabled.create', {
              item: t('exercises'),
            })}
          >
            <Button
              variant="outlined"
              onClick={() => setOpenCreateExerciseSheet?.(searchTerm)}
            >
              {t('createExercise')}
            </Button>
          </DisabledWithTooltip>
        ) : null
      }
      open={open}
      onClose={onClose}
      onAddItem={onAddExercise}
      items={exercises}
      isLoading={exercisesQuery.isLoading}
      isError={exercisesQuery.isError}
      renderCard={(item, viewMode, isSelected, setSelected) => {
        return (
          <ExerciseCard
            key={item.id}
            sx={isSelected ? { borderColor: blue[500], borderWidth: 2 } : {}}
            item={item}
            viewMode={viewMode}
            onClick={setSelected}
            showTags={showTags}
          />
        );
      }}
      renderTableView={(items, selectedItem, setSelected) => (
        <AdvancedSearchTable<LibraryExercise>
          items={items}
          selectedItem={selectedItem}
          setSelected={setSelected}
          onAddItem={onAddExercise}
          title={t('exercise')}
        />
      )}
      filters={
        <>
          <SearchInput
            fullWidth
            value={searchTerm}
            onChange={(v) => setSearchTerm(v)}
          />
          <Divider sx={{ my: 1, mb: 1 }} flexItem />
          <div className={'flex flex-col gap-4'}>
            <MultiSelect
              label={t('exerciseType')}
              placeholder={t('allExercises')}
              selectedOptions={types}
              options={trainingTypes}
              onChange={setTypes}
              optionToName={(option) => option.name}
            />
            <MultiSelect
              label={t('tags')}
              placeholder={t('allTags')}
              selectedOptions={selectedTags}
              options={tags}
              onChange={setSelectedTags}
              optionToName={(option) => option.name}
            />
          </div>
          <Divider sx={{ my: 1, mb: 1 }} flexItem />
          <div className={'flex flex-col gap-4'}>
            <MultiSelect
              label={t('bodyParts')}
              placeholder={t('allBodyParts')}
              selectedOptions={selectedBodyParts}
              options={bodyParts}
              onChange={setSelectedBodyParts}
              optionToName={(option) => t(option.name as any)}
            />
            <MultiSelect
              label={t('equipment')}
              placeholder={t('allEquipment')}
              selectedOptions={selectedEquipment}
              options={equipment}
              onChange={setSelectedEquipment}
              optionToName={(option) => option.name}
            />
            <Button
              disableRipple
              size="small"
              variant="text"
              sx={{
                color: 'highEmphasisTextLight',
                padding: 0,
                justifyContent: 'flex-start',
                '&:hover': {
                  boxShadow: 'none',
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
              onClick={() => {
                setSearchTerm('');
                setTypes([]);
                setSelectedTags([]);
                setSelectedBodyParts([]);
                setSelectedEquipment([]);
              }}
            >
              {t('resetFilters')}
            </Button>
          </div>
        </>
      }
    />
  );
};
